import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Hammer, MessageCircle, Phone } from "lucide-react";
import { usePageTitle } from "@/lib/usePageTitle";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { resolveImage } from "@/lib/media";
import { ShareButton } from "@/components/ShareButton";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ratioToPercent, tradeLabel } from "@/lib/format";

function TrustRow({ label, value }) {
  if (value === undefined || value === null) return null;
  return (
    <p className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </p>
  );
}

export default function WorkerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const [worker, setWorker] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageTitle(worker?.name);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/directory/workers/${id}`);
        setWorker(data);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="worker-not-found">
        <h1 className="font-heading text-2xl font-semibold">{t("business.notFoundTitle")}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t("business.notFoundBody")}</p>
        <Button className="mt-6 rounded-full" data-testid="worker-not-found-back" onClick={() => navigate("/")}>
          {t("business.notFoundBack")}
        </Button>
      </div>
    );
  if (!worker) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="worker-profile">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl bg-muted">
          {worker.photo_url ? (
            <img src={resolveImage(worker.photo_url)} alt={worker.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Hammer size={32} />
            </div>
          )}
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{worker.name}</h1>
          <p className="mt-1 text-base text-[hsl(var(--primary))]">{tradeLabel(lang, worker)}</p>
          {worker.experience_years > 0 && (
            <p className="mt-1 text-sm text-muted-foreground" data-testid="worker-experience">
              {t("directory.experience", { years: worker.experience_years, count: worker.experience_years })}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StarRating value={worker.rating_avg} count={worker.rating_count} />
            <span
              className={`text-xs font-medium ${worker.is_available ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
            >
              {worker.is_available ? t("directory.available") : t("directory.busy")}
            </span>
            <FavoriteButton targetType="worker" targetId={worker.id} />
            <ShareButton path={`/w/${worker.id}`} title={worker.name} testId="worker-share-btn" />
          </div>
          <div className="mt-4 flex gap-3">
            {worker.phone && (
              <Button asChild className="rounded-full" data-testid="worker-call-btn">
                <a href={`tel:${worker.phone}`}>
                  <Phone size={15} className="mr-2" /> {t("common.call")}
                </a>
              </Button>
            )}
            {worker.phone && (
              <Button asChild variant="outline" className="rounded-full" data-testid="worker-whatsapp-btn">
                <a href={`https://wa.me/${worker.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                  <MessageCircle size={15} className="mr-2" /> {t("common.whatsapp")}
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1.4fr_0.6fr]">
        <div className="space-y-6">
          {(worker.about || "").trim() && (
            <div>
              <h2 className="font-heading text-xl font-semibold">{t("common.about")}</h2>
              <p className="mt-2 leading-relaxed text-muted-foreground">{localized(lang, worker, "about")}</p>
            </div>
          )}
          {worker.areas && worker.areas.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("trust.areas")}
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {worker.areas.map((area) => (
                  <span
                    key={area}
                    className="rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3 py-1 text-xs font-medium text-[hsl(var(--primary))]"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-heading text-xl font-semibold">{t("business.tabReviews")}</h2>
            <div className="mt-4 space-y-4" data-testid="worker-review-list">
              {(!worker.ratings || worker.ratings.length === 0) && (
                <p className="text-muted-foreground">{t("business.noReviews")}</p>
              )}
              {(worker.ratings || []).map((r) => (
                <article key={r.id} className="rounded-2xl border border-border p-5" data-testid={`worker-review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{r.author_name}</p>
                    <StarRating value={r.rating} />
                  </div>
                  {r.body && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.body}</p>}
                </article>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-4 rounded-2xl border border-border p-6" data-testid="worker-trust-card">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("trust.title")}
          </h3>
          <TrustRow label={t("trust.jobsCompleted")} value={worker.completed_jobs} />
          {worker.completion_rate !== undefined && (
            <TrustRow label={t("trust.completionRate")} value={`${ratioToPercent(worker.completion_rate)}%`} />
          )}
          {worker.avg_response_minutes !== undefined && (
            <TrustRow label={t("trust.responseTime")} value={t("trust.minutes", { count: worker.avg_response_minutes })} />
          )}
          {worker.repeat_customers !== undefined && (
            <TrustRow label={t("trust.repeatCustomers")} value={worker.repeat_customers} />
          )}
        </aside>
      </div>
    </div>
  );
}
